import { useState, useEffect } from 'react';
import isEqual from 'lodash.isequal';
import {
  OnSelectChange,
  Select,
  SelectListPosition,
  SelectType,
  Textarea,
} from '@ui/src';
import Card from '../../partial/card/card';
import RealMonthlyVolumes from '../../partial/aml/real-monthly-volumes';
import BusinessPartners from './business-partners';
import { AMLInitialCddTypes, CddFormValues } from '../types';

import cn from './aml.module.css';

const InitialCdd = ({
  isLoading,
  businessPartners,
  baseCurrency,
  onUpdateRange,
  status,
  clientScheme,
  baseInformation,
  currencies,
  updateRangesInProgress,
  onUpdateCorporateInfo,
  updateInfoIsLoading,
}: AMLInitialCddTypes) => {
  const [defaultCddValues, setDefaultCddValues] = useState<CddFormValues>({});
  const [cddFormValues, setCddFormValues] = useState<CddFormValues>({});

  const isDirty = !isEqual(defaultCddValues, cddFormValues);

  const onChange = (
    e: React.ChangeEvent<HTMLInputElement> | OnSelectChange,
  ) => {
    const { name, value } = e.target;

    const nextCddFormValues = { ...cddFormValues };

    // @ts-ignore
    nextCddFormValues[name] = value;

    setCddFormValues(nextCddFormValues);
  };

  const onSaveChanges = () => {
    onUpdateRange({
      monthlyMaxValueIncomingPaymentRangeId:
        cddFormValues?.monthlyMaxValueIncomingPaymentRangeId as string,
      monthlyMaxValueOutgoingPaymentRangeId:
        cddFormValues?.monthlyMaxValueOutgoingPaymentRangeId as string,
      monthlyNumberIncomingTransactionsRangeId:
        cddFormValues?.monthlyNumberIncomingTransactionsRangeId as string,
      monthlyNumberOutgoingTransactionsRangeId:
        cddFormValues?.monthlyNumberOutgoingTransactionsRangeId as string,
      monthlyTurnoverRangeId: cddFormValues?.monthlyTurnoverRangeId as string,
      monthlyValueIncomingTransactionsRangeId:
        cddFormValues?.monthlyValueIncomingTransactionsRangeId as string,
      monthlyValueOutgoingTransactionsRangeId:
        cddFormValues?.monthlyValueOutgoingTransactionsRangeId as string,
    });

    if (
      clientScheme?.purposeOfAccountActive ||
      clientScheme?.amlCtfComplianceProgramActive ||
      clientScheme?.expectedTypeOfClientsActive ||
      clientScheme?.requiredCurrenciesCryptoActive
    ) {
      onUpdateCorporateInfo({
        purposeOfAccount: cddFormValues?.purposeOfAccount,
        expectedTypeOfClients: cddFormValues?.expectedTypeOfClients,
        requiredCurrenciesCryptoId: cddFormValues?.requiredCurrenciesCryptoId,
        amlCtfComplianceProgram: cddFormValues?.amlCtfComplianceProgram,
      });
    }
  };

  useEffect(() => {
    if (baseInformation) {
      const body: CddFormValues = {
        monthlyMaxValueIncomingPaymentRangeId:
          baseInformation?.monthlyMaxValueIncomingPaymentRangeId,
        monthlyMaxValueOutgoingPaymentRangeId:
          baseInformation?.monthlyMaxValueOutgoingPaymentRangeId,
        monthlyNumberIncomingTransactionsRangeId:
          baseInformation?.monthlyNumberIncomingTransactionsRangeId,
        monthlyNumberOutgoingTransactionsRangeId:
          baseInformation?.monthlyNumberOutgoingTransactionsRangeId,
        monthlyTurnoverRangeId: baseInformation?.monthlyTurnoverRangeId,
        monthlyValueIncomingTransactionsRangeId:
          baseInformation?.monthlyValueIncomingTransactionsRangeId,
        monthlyValueOutgoingTransactionsRangeId:
          baseInformation?.monthlyValueOutgoingTransactionsRangeId,
      };

      if (
        clientScheme?.purposeOfAccountActive ||
        clientScheme?.amlCtfComplianceProgramActive ||
        clientScheme?.expectedTypeOfClientsActive ||
        clientScheme?.requiredCurrenciesCryptoActive
      ) {
        if (clientScheme?.purposeOfAccountActive) {
          body.purposeOfAccount = baseInformation?.purposeOfAccount;
        }

        if (clientScheme?.amlCtfComplianceProgramActive) {
          body.amlCtfComplianceProgram =
            baseInformation?.amlCtfComplianceProgram !== null &&
            baseInformation?.amlCtfComplianceProgram
              ? 'yes'
              : 'no';
        }
      }

      if (clientScheme?.expectedTypeOfClientsActive) {
        body.expectedTypeOfClients = baseInformation?.expectedTypeOfClients;
      }

      if (clientScheme?.requiredCurrenciesCryptoActive) {
        body.requiredCurrenciesCryptoId =
          baseInformation?.requiredCurrenciesCryptoId;
      }

      setCddFormValues(body);
      setDefaultCddValues(body);
    }
  }, [baseInformation, clientScheme]);

  return (
    <Card
      isLoading={isLoading}
      title="Initial CDD"
      className={cn.initialCddCard}
    >
      <>
        <div className={cn.realMonthlyVolumeCorporate}>
          <RealMonthlyVolumes
            title={`Indicated monthly turnover (${baseCurrency})`}
            monthlyMaxValueIncomingPaymentRangeId={
              cddFormValues?.monthlyMaxValueIncomingPaymentRangeId
            }
            monthlyMaxValueOutgoingPaymentRangeId={
              cddFormValues?.monthlyMaxValueOutgoingPaymentRangeId
            }
            monthlyNumberIncomingTransactionsRangeId={
              cddFormValues?.monthlyNumberIncomingTransactionsRangeId
            }
            monthlyNumberOutgoingTransactionsRangeId={
              cddFormValues?.monthlyNumberOutgoingTransactionsRangeId
            }
            monthlyTurnoverRangeId={cddFormValues?.monthlyTurnoverRangeId}
            monthlyValueIncomingTransactionsRangeId={
              cddFormValues?.monthlyValueIncomingTransactionsRangeId
            }
            monthlyValueOutgoingTransactionsRangeId={
              cddFormValues?.monthlyValueOutgoingTransactionsRangeId
            }
            status={status}
            corporate
            onChange={onChange}
            saveButtonDisabled={
              !isDirty || updateRangesInProgress || updateInfoIsLoading
            }
            requestInProgress={updateRangesInProgress || updateInfoIsLoading}
            onSaveChanges={onSaveChanges}
          />
        </div>
        <BusinessPartners
          businessPartners={businessPartners}
          baseCurrency={baseCurrency}
          clientScheme={clientScheme}
        />
        {(clientScheme?.purposeOfAccountActive ||
          clientScheme?.amlCtfComplianceProgramActive ||
          clientScheme?.expectedTypeOfClientsActive ||
          clientScheme?.requiredCurrenciesCryptoActive) && (
          <>
            <div className={cn.businessPartnersTitle}>
              Business & Compliance Profile
            </div>
            {clientScheme?.purposeOfAccountActive && (
              <div className={cn.realMonthlyVolumeWrapper}>
                <div
                  className={`${cn.realMonthlyVolume} ${cn.realMonthlyVolumeCorporate}`}
                >
                  <div className={cn.realMonthlyVolumeTitle}>
                    Purpose of account
                  </div>
                  <div className={cn.realMonthlyVolumeValue}>
                    <Textarea
                      name="purposeOfAccount"
                      value={cddFormValues?.purposeOfAccount}
                      placeholder="Describe Purpose of account"
                      className={`${cn.purposeOfAccount} borderless`}
                      onChange={onChange}
                    />
                  </div>
                </div>
              </div>
            )}
            {clientScheme?.expectedTypeOfClientsActive && (
              <div className={cn.realMonthlyVolumeWrapper}>
                <div
                  className={`${cn.realMonthlyVolume} ${cn.realMonthlyVolumeCorporate}`}
                >
                  <div className={cn.realMonthlyVolumeTitle}>
                    Expected types of clients
                  </div>
                  <div className={cn.realMonthlyVolumeValue}>
                    <Select
                      name="expectedTypeOfClients"
                      value={cddFormValues?.expectedTypeOfClients}
                      list={[
                        {
                          name: 'B2B',
                          value: 'B2B',
                        },
                        {
                          name: 'B2C',
                          value: 'B2C',
                        },
                      ]}
                      placeholder="Select types"
                      selectWrapperClassName={`${cn.expandableRowContentSelect} ${cn.disabledSelect} ${cn.monthlyVolumeSelector}`}
                      mode={SelectType.MULTIPLE}
                      selectListPosition={SelectListPosition.TOP}
                      onChange={onChange}
                      borderless
                    />
                  </div>
                </div>
              </div>
            )}
            {clientScheme?.expectedTypeOfClientsActive && (
              <div className={cn.realMonthlyVolumeWrapper}>
                <div
                  className={`${cn.realMonthlyVolume} ${cn.realMonthlyVolumeCorporate}`}
                >
                  <div className={cn.realMonthlyVolumeTitle}>
                    Required currencies
                  </div>
                  <div className={cn.realMonthlyVolumeValue}>
                    <Select
                      name="requiredCurrenciesCryptoId"
                      value={cddFormValues?.requiredCurrenciesCryptoId}
                      list={currencies}
                      placeholder="Select currencies"
                      selectWrapperClassName={`${cn.expandableRowContentSelect} ${cn.disabledSelect} ${cn.monthlyVolumeSelector}`}
                      mode={SelectType.MULTIPLE}
                      selectListPosition={SelectListPosition.TOP}
                      onChange={onChange}
                      borderless
                    />
                  </div>
                </div>
              </div>
            )}
            {clientScheme?.amlCtfComplianceProgramActive && (
              <div className={cn.realMonthlyVolumeWrapper}>
                <div
                  className={`${cn.realMonthlyVolume} ${cn.realMonthlyVolumeCorporate}`}
                >
                  <div className={cn.realMonthlyVolumeTitle}>
                    Do you have a AML/CTF Compliance Programm
                  </div>
                  <div className={cn.realMonthlyVolumeValue}>
                    <Select
                      name="amlCtfComplianceProgram"
                      value={cddFormValues?.amlCtfComplianceProgram}
                      list={[
                        {
                          name: 'Yes',
                          value: 'yes',
                        },
                        {
                          name: 'No',
                          value: 'no',
                        },
                      ]}
                      placeholder="Select option"
                      selectWrapperClassName={`${cn.expandableRowContentSelect} ${cn.disabledSelect} ${cn.monthlyVolumeSelector} ${cn.fixHeight}`}
                      selectListPosition={SelectListPosition.TOP}
                      onChange={onChange}
                      borderless
                    />
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </>
    </Card>
  );
};

export default InitialCdd;
